import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, Copy, Check, List } from "lucide-react";
import TopHeader from "../components/TopHeader";
import Footer from "../components/Footer";
import SeoHead from "../components/SeoHead";
import { getBlogPostBySlug, type Block } from "../data/blogData";
import { DIAGRAMS } from "../components/BlogDiagrams";
import NotFound from "./notfound";

// ── Inline markdown parser ────────────────────────────────────────────────────
// Handles **bold**, `code`, and [text](url) in paragraph / list text.

function parseInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[2]) {
      parts.push(<strong key={key++} className="font-semibold text-foreground">{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(
        <code key={key++} className="font-mono text-[0.85em] bg-muted px-1.5 py-0.5 rounded text-foreground">
          {match[3]}
        </code>
      );
    } else if (match[4] && match[5]) {
      parts.push(
        <a key={key++} href={match[5]} className="text-primary underline underline-offset-2 hover:opacity-80 transition-opacity">
          {match[4]}
        </a>
      );
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        });
      }}
      className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors text-zinc-300"
    >
      {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ── Callout ───────────────────────────────────────────────────────────────────

const CALLOUT_STYLES = {
  tip:     { border: "border-blue-400/40",   bg: "bg-blue-50 dark:bg-blue-950/30",   icon: "💡", label: "Tip",     text: "text-blue-700 dark:text-blue-300" },
  warn:    { border: "border-amber-400/40",  bg: "bg-amber-50 dark:bg-amber-950/30", icon: "⚠️", label: "Warning", text: "text-amber-700 dark:text-amber-300" },
  insight: { border: "border-violet-400/40", bg: "bg-violet-50 dark:bg-violet-950/30", icon: "🔍", label: "Insight", text: "text-violet-700 dark:text-violet-300" },
  note:    { border: "border-slate-400/40",  bg: "bg-slate-50 dark:bg-slate-900/40", icon: "📌", label: "Note",    text: "text-slate-700 dark:text-slate-300" },
};

// ── Block renderer ────────────────────────────────────────────────────────────

function RenderBlock({ block }: { block: Block }) {
  switch (block.t) {
    case "h2":
      return (
        <h2
          id={block.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
          className="text-2xl font-bold text-foreground mt-12 mb-4 scroll-mt-24"
        >
          {block.text}
        </h2>
      );

    case "h3":
      return (
        <h3
          id={block.text.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
          className="text-lg font-semibold text-foreground mt-8 mb-3 scroll-mt-24"
        >
          {block.text}
        </h3>
      );

    case "p":
      return (
        <p className="text-base text-foreground/90 leading-[1.85] mb-5">
          {parseInline(block.text)}
        </p>
      );

    case "code":
      return (
        <div className="my-6 rounded-xl overflow-hidden border border-zinc-800">
          <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
            <span className="text-[11px] font-mono text-zinc-500">{block.lang}</span>
            <CopyBtn text={block.src} />
          </div>
          <pre className="bg-zinc-950 text-zinc-100 text-[13px] leading-relaxed p-5 overflow-x-auto font-mono">
            <code>{block.src}</code>
          </pre>
        </div>
      );

    case "callout": {
      const s = CALLOUT_STYLES[block.kind];
      return (
        <div className={`my-6 flex gap-3 rounded-xl border ${s.border} ${s.bg} px-5 py-4`}>
          <span className="text-lg shrink-0 mt-0.5">{s.icon}</span>
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${s.text}`}>{s.label}</p>
            <p className="text-sm text-foreground/80 leading-relaxed">{parseInline(block.text)}</p>
          </div>
        </div>
      );
    }

    case "diagram": {
      const Diagram = DIAGRAMS[block.id];
      return Diagram ? <Diagram /> : null;
    }

    case "list":
      return (
        <ul className={`my-5 space-y-2 ${block.ordered ? "list-none" : "list-none"}`}>
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-foreground/85 leading-relaxed text-base">
              {block.ordered ? (
                <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0 mt-1">
                  {i + 1}
                </span>
              ) : (
                <span className="w-1.5 h-1.5 rounded-full bg-primary/60 mt-2.5 shrink-0" />
              )}
              <span>{parseInline(item)}</span>
            </li>
          ))}
        </ul>
      );

    case "quote":
      return (
        <blockquote className="my-8 border-l-4 border-primary/40 pl-6 py-1">
          <p className="text-xl text-foreground/80 leading-relaxed italic">{block.text}</p>
          {block.attribution && (
            <footer className="mt-2 text-sm text-muted-foreground">— {block.attribution}</footer>
          )}
        </blockquote>
      );

    case "divider":
      return <hr className="my-10 border-border" />;

    default:
      return null;
  }
}

// ── Reading progress bar ──────────────────────────────────────────────────────

function ReadingProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrolled = el.scrollTop;
      const total = el.scrollHeight - el.clientHeight;
      setPct(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-[3px] bg-transparent">
      <div
        className="h-full bg-gradient-to-r from-blue-500 to-violet-500 transition-[width] duration-75"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ── Table of contents ─────────────────────────────────────────────────────────

function Toc({ blocks }: { blocks: Block[] }) {
  const headings = blocks.filter((b): b is Extract<Block, { t: "h2" | "h3" }> =>
    b.t === "h2" || b.t === "h3"
  );
  const [active, setActive] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: "-10% 0% -80% 0%" }
    );
    document.querySelectorAll("h2[id], h3[id]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  if (headings.length < 3) return null;

  return (
    <nav className="hidden xl:block fixed top-24 right-8 w-56">
      <div className="flex items-center gap-2 mb-3">
        <List className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contents</span>
      </div>
      <ul className="space-y-1">
        {headings.map((h) => {
          const id = h.text.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          const isActive = active === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                className={`block text-[12px] leading-snug py-1 transition-colors ${
                  h.t === "h3" ? "pl-3" : ""
                } ${isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground"}`}
              >
                {h.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug = "" } = useParams();
  const post = getBlogPostBySlug(slug);
  const contentRef = useRef<HTMLDivElement>(null);

  if (!post) return <NotFound />;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SeoHead
        title={`${post.title} — API to MCP Converter`}
        description={post.description}
        canonical={`/blog/${post.slug}`}
        image={post.ogImage}
      />
      <ReadingProgress />
      <TopHeader />

      {/* Cover */}
      <div className={`bg-gradient-to-br ${post.coverGradient}`}>
        <div className="max-w-3xl mx-auto px-4 pt-16 pb-14">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-800 text-sm mb-8 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> All posts
            </Link>

            <div className="flex flex-wrap gap-2 mb-5">
              {post.tags.map(tag => (
                <span key={tag} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-800/10 text-gray-700">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-5">
              {post.title}
            </h1>

            <p className="text-gray-600 text-base leading-relaxed mb-6 max-w-2xl">
              {post.description}
            </p>

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span>{formatDate(post.publishedAt)}</span>
              <span>·</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {post.readingMins} min read
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1">
          {/* Article */}
          <motion.article
            ref={contentRef}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="max-w-2xl mx-auto px-4 py-12"
          >
            {post.blocks.map((block, i) => (
              <RenderBlock key={i} block={block} />
            ))}

            {/* Footer CTA */}
            <div className="mt-14 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 p-8 text-white text-center">
              <p className="font-bold text-lg mb-2">Try it yourself</p>
              <p className="text-blue-100 text-sm mb-5">
                Convert any OpenAPI or REST spec into a live MCP server in under two minutes.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-white text-blue-600 font-semibold text-sm hover:bg-blue-50 transition-colors"
              >
                Convert your API →
              </Link>
            </div>

            {/* Post nav */}
            <div className="mt-8 pt-6 border-t border-border">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to all posts
              </Link>
            </div>
          </motion.article>

          {/* TOC sidebar */}
          <Toc blocks={post.blocks} />
      </div>

      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.description,
            "datePublished": post.publishedAt,
            "url": `https://apitomcpconverter.com/blog/${post.slug}`,
            "publisher": {
              "@type": "Organization",
              "name": "API to MCP Converter",
              "url": "https://apitomcpconverter.com",
            },
          }),
        }}
      />

      <Footer />
    </div>
  );
}
